import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadDocumentFromSource, saveECMRWithSync } from '../services/eCMRService';
import { ECMR } from '../types/eCMR';
import ECMRForm from './ECMRForm';
import { safeConsoleError } from '../utils/safeConsole';
import { validateCmrDocument } from '../compliance/validateCmrDocument';

const EditECMR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ecmr, setEcmr] = useState<ECMR | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Cloud-only migration: editing is always backed by the authenticated API.
  // Legacy source=local query parameters are intentionally ignored.
  const source: 'api' = 'api';
  
  useEffect(() => {
    if (!id) {
      setError('ID de e-CMR no proporcionado');
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      try {
        const loadedEcmr = await loadDocumentFromSource(id, source);
        if (!loadedEcmr) {
          setError('e-CMR no encontrado');
          setLoading(false);
          return;
        }
        
        setEcmr(loadedEcmr);
        setLoading(false);
      } catch (err) {
        setError('No se pudo cargar el documento e-CMR. Inténtelo de nuevo o vuelva a la lista.');
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, source]);
  
  const handleSubmit = async (data: ECMR) => {
    setSaving(true);
    try {
      const cmrValidation = validateCmrDocument(data);
      const blockingErrorCount = cmrValidation.errors.filter((validationError) => validationError.severity === 'error').length;
      if (!cmrValidation.valid || blockingErrorCount > 0) {
        alert(`No se pudo guardar el e-CMR: faltan ${blockingErrorCount} requisitos CMR/eCMR obligatorios. Revise los campos obligatorios antes de guardar.`);
        return;
      }

      // Save document through the cloud API only.
      const result = await saveECMRWithSync(
        data, 
        data.ExchangedDocument?.ID || 'e-CMR Editado',
        id,
        source
      );
      
      if (result.success && result.apiDocument) {
        navigate(`/view/${id}?source=api`);
      } else {
        alert('No se pudo guardar el e-CMR en la nube. Inicia sesión y vuelva a intentarlo.');
      }
    } catch (error) {
      safeConsoleError('Error al guardar el e-CMR', 'document.edit.save');
      alert('Error al guardar el e-CMR');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    navigate(`/view/${id}?source=api`);
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-lg">Cargando documento...</p>
        </div>
      </div>
    );
  }
  
  if (error || !ecmr) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <h3 className="font-bold">Error al cargar el documento</h3>
          <p>{error || 'Error desconocido al cargar el e-CMR'}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver a la lista
        </button>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">
          Editar e-CMR: {ecmr.ExchangedDocument?.ID || 'Sin título'}
        </h1>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            ☁️ Documento en la nube
          </span>
          {saving && (
            <span className="text-blue-600">
              <i className="fas fa-spinner fa-spin mr-1"></i>
              Guardando...
            </span>
          )}
        </div>
      </div>
      
      <ECMRForm
        initialData={ecmr}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        disabled={saving}
      />
    </div>
  );
};

export default EditECMR;
